/**
 * Simulation Panel
 * Visual lifecycle stage diagram with action triggers
 */

import { useState, useEffect } from 'react'
import { Button } from '@/components/admin/ui/button'
import { Badge } from '@/components/admin/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card'
import { useToast } from '@/hooks/use-toast'
import { emailTestService, StageAction } from '@/services/emailTestService'
import { Loader2, Mail, ChevronRight, Info, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SimulationPanelProps {
  adminEmail: string
}

// Stage colors matching the lifecycle
const STAGE_COLORS: Record<string, string> = {
  booked: 'bg-pink-200 border-pink-400',
  logistics_questionnaire_completed: 'bg-purple-200 border-purple-400',
  client_talent_communication_in_progress: 'bg-blue-200 border-blue-400',
  talent_5_day_check_in: 'bg-indigo-200 border-indigo-400',
  event_day: 'bg-yellow-200 border-yellow-400',
  media_uploaded: 'bg-pink-400 border-pink-600',
  media_issue: 'bg-red-400 border-red-600',
  media_approved: 'bg-green-300 border-green-500',
  talent_paid: 'bg-emerald-300 border-emerald-500',
  media_delivered_partial: 'bg-teal-300 border-teal-500',
  media_delivered_full: 'bg-cyan-300 border-cyan-500',
  booking_complete: 'bg-green-500 border-green-700'
}

export const SimulationPanel = ({ adminEmail }: SimulationPanelProps) => {
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [stages, setStages] = useState<StageAction[]>([])
  const [selectedStage, setSelectedStage] = useState<string | null>(null)
  const [sendingEmail, setSendingEmail] = useState<string | null>(null)

  useEffect(() => {
    loadStages()
  }, [])

  const loadStages = async () => {
    setLoading(true)
    try {
      const data = await emailTestService.getSimulationStages()
      setStages(data)
      if (data.length > 0) {
        setSelectedStage(data[0].id)
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load simulation stages',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleTriggerEmail = async (emailType: string) => {
    setSendingEmail(emailType)
    try {
      await emailTestService.triggerSimulationAction(
        'send_email',
        null,
        adminEmail,
        { emailType }
      )
      toast({
        title: 'Success',
        description: `Test email sent to ${adminEmail}`
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to trigger email',
        variant: 'destructive'
      })
    } finally {
      setSendingEmail(null)
    }
  }

  const selectedStageData = stages.find(s => s.id === selectedStage)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading simulation data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stage Progress Visualization */}
      <div className="relative">
        <div className="flex items-center overflow-x-auto pb-4 gap-2">
          {stages.map((stage, index) => (
            <div key={stage.id} className="flex items-center">
              <button
                onClick={() => setSelectedStage(stage.id)}
                className={cn(
                  'flex-shrink-0 px-3 py-2 rounded-lg border-2 transition-all text-sm font-medium',
                  STAGE_COLORS[stage.id] || 'bg-gray-200 border-gray-400',
                  selectedStage === stage.id && 'ring-2 ring-offset-2 ring-primary',
                  'hover:opacity-80'
                )}
              >
                <span className="whitespace-nowrap">{stage.label}</span>
              </button>
              {index < stages.length - 1 && (
                <ChevronRight className="h-4 w-4 text-muted-foreground mx-1 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Selected Stage Details */}
      {selectedStageData && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Badge className={cn(STAGE_COLORS[selectedStageData.id], 'text-sm')}>
                {selectedStageData.label}
              </Badge>
            </div>
            <CardDescription className="mt-2 flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {selectedStageData.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Emails for this stage */}
            {selectedStageData.emailDetails && selectedStageData.emailDetails.length > 0 ? (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Emails Triggered at This Stage
                </h4>
                <div className="grid gap-3">
                  {selectedStageData.emailDetails.map(email => (
                    <div
                      key={email.type}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{email.label}</p>
                        <p className="text-sm text-muted-foreground">{email.description}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTriggerEmail(email.type)}
                        disabled={sendingEmail === email.type}
                      >
                        {sendingEmail === email.type ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Mail className="h-4 w-4 mr-1" />
                            Test
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No automated emails at this stage</p>
                <p className="text-sm">This stage is a workflow checkpoint</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h4 className="font-medium mb-3">How Simulation Works</h4>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-primary">1.</span>
            Click on any stage to see what actions/emails are triggered at that point
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">2.</span>
            Use the "Test" button to send that email type to your inbox ({adminEmail})
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">3.</span>
            For full testing, create a Test Booking and advance it through the stages
          </li>
        </ul>
      </div>
    </div>
  )
}
